import { trace } from 'owa-trace';
import { HttpReadyState, isSuccessStatusCode } from 'owa-http-status-codes';
import discoverTodoServiceModel, {
    ServiceModel,
} from 'owa-todo-service/lib/utils/discoverTodoServiceModel';
import { parseResponse } from './parseResponse';
import { setHeadersOnRequest } from './setHeadersOnRequest';
import {
    addSubscription,
    removeSubscription,
    getSubscriptionsCount,
    notifySubscribers,
    createSubscriptionCache,
    SubscriptionsCache,
} from './subscriptions';
import {
    createRetriableFunction,
    createExponentialBackoffFunction,
    RetriableFunction,
} from 'owa-retriable-function';
import type { TaskSubscriptionData } from '../schema/TaskSubscriptionData';
import type { ToDoNotification } from '../schema/ToDoNotification';
import { isToDoNotification } from './typeGuards';

let xhr: XMLHttpRequest;
let previousXhr: XMLHttpRequest;

let currentRequest: RetriableFunction<void> | undefined;

let todoServiceModel: ServiceModel;
let servicePollId: number;

const TWO_MINUTES_IN_MS = 2 * 60 * 1000;

const createRetriableFunc = createRetriableFunction({
    maximumAttempts: 3,
    timeBetweenRetryInMS: createExponentialBackoffFunction(1000),
});

let subCache: SubscriptionsCache<ToDoNotification>;

export function subscribeNotifications(
    subscriptionId: string,
    subscriptionData: TaskSubscriptionData<ToDoNotification>
) {
    if (!subCache) {
        subCache = createSubscriptionCache<ToDoNotification>();
    }
    addSubscription<ToDoNotification>(subCache, subscriptionId, subscriptionData);

    initializeSubscriptionChannel();
}

export async function unSubscribeNotifications(subscriptionId: string) {
    if (!subCache) {
        return;
    }
    // remove from the subscriptions list
    removeSubscription(subCache, subscriptionId);

    // if there are not subscriptions in the list, end the connection
    if (getSubscriptionsCount(subCache) === 0) {
        xhr?.abort();
        previousXhr?.abort();
        currentRequest = undefined;
    }
}

async function initializeSubscriptionChannel(forceReconnect?: boolean): Promise<void> {
    // exit early if a request is already active, unless we are planning to force a reconnect
    if (currentRequest) {
        if (!forceReconnect) {
            return;
        } else {
            currentRequest.cancel();
        }
    }

    try {
        currentRequest = createRetriableFunc(subscribeInternal);
        await currentRequest.retriableFunc();
    } catch (error) {
        trace.warn(`[owa-todo-notifications] subscribe: subscribe with retry failed: ${error}`);
        currentRequest = undefined;
    }
}

async function subscribeInternal(): Promise<void> {
    await initializeServiceCutoverPoll();

    return new Promise((resolve, reject) => {
        // if there is already a channel, track as previous since will set up a new one
        if (xhr) {
            previousXhr = xhr;
        }

        xhr = new XMLHttpRequest();
        xhr.onload = () => {
            if (isSuccessStatusCode(xhr.status)) {
                resolve();
            } else {
                reject();
            }
        };

        xhr.onerror = () => {
            reject();
        };

        xhr.ontimeout = () => {
            reject();
        };

        // TODO VSO 79588: Consume todo notifications through the owa notification channel
        const todoNotificationEndpoint = `https://substrate.office.com/${todoServiceModel}/api/v1/realtime`;
        xhr.open('GET', todoNotificationEndpoint);

        let seenBytes = 0;
        xhr.onreadystatechange = () => {
            // on connection, end the previous connection (if any)
            if (xhr.readyState === HttpReadyState.HeadersReceived) {
                if (previousXhr && previousXhr.readyState > HttpReadyState.Opened) {
                    previousXhr.abort();
                }
            }

            if (xhr.readyState > HttpReadyState.HeadersReceived) {
                // The responseText object is additive. So we use seenBytes,
                // to keep track of how much data in responseText we have already processed
                const newData: string = xhr.responseText.substring(seenBytes);
                const { unparsedBytes, parsedItems } = parseResponse(newData);
                seenBytes = xhr.responseText.length - unparsedBytes;
                if (parsedItems) {
                    const todoNotifications = parsedItems.filter(isToDoNotification);
                    todoNotifications.forEach(notification =>
                        notifySubscribers(subCache, notification)
                    );
                }
            }
        };

        // store local reference to opened request in case of any state changes while set-headers promise is pending
        // (e.g. due to force-reconnect logic)
        let openedXhr = xhr;
        setHeadersOnRequest(openedXhr).then(() => {
            // as an extra precaution, ensure that the request has already been opened before initiating
            if (openedXhr.readyState === HttpReadyState.Opened) {
                openedXhr.send();
            }
        });
    });
}

async function initializeServiceCutoverPoll() {
    // query and cache initial service to use
    if (!todoServiceModel) {
        todoServiceModel = await discoverTodoServiceModel();
    }

    // set up service polling interval and handle any detected cutovers
    if (!servicePollId) {
        servicePollId = window.setInterval(async () => {
            // check for cutover and track which service to use
            let isCutoverDetected: boolean = false;
            const latestResult = await discoverTodoServiceModel();
            if (latestResult !== todoServiceModel) {
                todoServiceModel = latestResult;
                isCutoverDetected = true;
            }

            // B2 service does not support sticky sessions so traffic might be routed to different machines
            // if a cutover occurs or we are calling B2 service, initialize a new subscription channel
            if (isCutoverDetected || todoServiceModel === 'todob2') {
                initializeSubscriptionChannel(true /* forceReconnect */);
            }
        }, TWO_MINUTES_IN_MS);
    }
}
